import 'react-toastify/dist/ReactToastify.min.css'
import { createBrowserRouter, LoaderFunctionArgs, redirect, RouterProvider } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Footer, NoMatch } from '~components'
import { useAuth } from '~hooks'
import {
  AdminMovedPage,
  DashboardPage,
  ExportPage,
  LoginPage,
  MyProfilePage,
  RequestPasswordResetPage,
  ResetPasswordPage,
  RoleAddPage,
  RoleEditPage,
  RolesListPage,
  SalesListPage,
  TicketCategoriesListPage,
  TicketCategoryAddPage,
  TicketCategoryEditPage,
  TicketTypeAddPage,
  TicketTypeEditPage,
  TicketTypesListPage,
  TransactionsListPage,
  TransactionViewPage,
  UserAddPage,
  UserEditPage,
  UsersListPage,
  VouchersListPage,
} from '~pages'
import { SettingsPage } from '~pages/Settings'
import { ADMIN_MOVED, navUrls } from '~constants'

export const Main = () => {
  const auth = useAuth()
  const { i18n } = useTranslation()

  const storedLocale = localStorage.getItem('locale')
  if (storedLocale && storedLocale !== i18n.language) {
    i18n.changeLanguage(storedLocale)
  }

  // Admin relocated: short-circuit the whole router with the "moved" notice.
  if (ADMIN_MOVED) {
    return <AdminMovedPage />
  }

  const loginLoader = ({ request }: LoaderFunctionArgs) => {
    const url = new URL(request.url)
    const fromPath = url.searchParams.get('from')
    if (auth.loggedIn) {
      return redirect(fromPath || '/')
    }
    return null
  }

  const protectedLoader = async (context: LoaderFunctionArgs) => {
    const { pathname } = new URL(context.request.url)
    const token = localStorage.getItem('token')

    // TODO: include one more check to validate the token is not expired
    if (!token || token === 'undefined' || token === 'null') {
      await auth.logout()
      return redirect(`/login?from=${pathname}`)
    }
    return null
  }

  const router = createBrowserRouter([
    {
      path: navUrls.dashboard(),
      loader: (context) => protectedLoader(context),
      element: <DashboardPage />,
      children: [
        {
          path: navUrls.export(),
          loader: (context) => protectedLoader(context),
          element: <ExportPage />,
        },
      ],
    },
    {
      path: navUrls.categories(),
      loader: (context) => protectedLoader(context),
      element: <TicketCategoriesListPage />,
      children: [
        {
          path: navUrls.categoryAdd(),
          loader: (context) => protectedLoader(context),
          element: <TicketCategoryAddPage />,
        },
        {
          path: navUrls.categoryEdit(':ticketCategoryId'),
          loader: (context) => protectedLoader(context),
          element: <TicketCategoryEditPage />,
        },
      ],
    },
    {
      path: navUrls.tickets(':categoryId'),
      loader: (context) => protectedLoader(context),
      element: <TicketTypesListPage />,
      children: [
        {
          path: navUrls.ticketAdd(':categoryId'),
          loader: (context) => protectedLoader(context),
          element: <TicketTypeAddPage />,
        },
        {
          path: navUrls.ticketEdit(':categoryId', ':ticketTypeId'),
          loader: (context) => protectedLoader(context),
          element: <TicketTypeEditPage />,
        },
      ],
    },
    {
      path: navUrls.users(),
      loader: (context) => protectedLoader(context),
      element: <UsersListPage />,
      children: [
        {
          path: navUrls.userAdd(),
          loader: (context) => protectedLoader(context),
          element: <UserAddPage />,
        },
        {
          path: navUrls.userEdit(':userId'),
          loader: (context) => protectedLoader(context),
          element: <UserEditPage />,
        },
      ],
    },
    {
      path: navUrls.roles(),
      loader: (context) => protectedLoader(context),
      element: <RolesListPage />,
      children: [
        {
          path: navUrls.roleAdd(),
          loader: (context) => protectedLoader(context),
          element: <RoleAddPage />,
        },
        {
          path: navUrls.roleEdit(':roleId'),
          loader: (context) => protectedLoader(context),
          element: <RoleEditPage />,
        },
      ],
    },
    {
      path: navUrls.transactions(),
      loader: (context) => protectedLoader(context),
      element: <TransactionsListPage />,
      children: [
        {
          path: '/transactions/:transactionId',
          loader: (context) => protectedLoader(context),
          element: <TransactionViewPage />,
        },
      ],
    },
    {
      path: navUrls.settings(),
      loader: (context) => protectedLoader(context),
      element: <SettingsPage />,
    },
    {
      path: navUrls.vouchers(),
      loader: (context) => protectedLoader(context),
      element: <VouchersListPage />,
    },
    {
      path: '/sales',
      loader: (context) => protectedLoader(context),
      element: <SalesListPage />,
    },
    {
      path: navUrls.login(),
      loader: loginLoader,
      element: <LoginPage />,
    },
    {
      path: '/profile',
      loader: (context) => protectedLoader(context),
      element: <MyProfilePage />,
    },
    {
      path: '/request-password-reset',
      element: <RequestPasswordResetPage />,
    },
    {
      path: '/reset-password',
      element: <ResetPasswordPage />,
    },
    {
      path: '/*',
      element: <NoMatch />,
    },
  ])

  return (
    <>
      <RouterProvider router={router} />
      <Footer />
    </>
  )
}
